import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useDynamicManifest } from '../hooks/useDynamicManifest';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import FloatingAssistant from './FloatingAssistant';

const DEFAULT_TITLE = '1000AD | AI Business Intelligence Manager';

const PAGE_TITLES = {
  '/home': 'Home',
  '/assistant': 'AI Assistant',
  '/insights': 'Insights',
  '/inventory': 'Inventory',
  '/sales': 'Sales',
  '/expenses': 'Expenses',
  '/billing': 'Billing',
  '/profile': 'Profile',
  '/sales-strategy': 'AI Sales Strategy',
  '/managers/ceo': 'AI Managers',
  '/erp': 'Smart ERP Engine',
  '/erp/journal': 'Journal Entries',
  '/erp/ledger': 'Account Ledger',
  '/erp/aging': 'Aging Report',
  '/erp/gst': 'GST Returns',
  '/erp/bank': 'Bank Reconciliation',
  '/boardroom': 'AI Boardroom',
  '/multi-branch': 'Multi-Branch Ledger',
  '/integrations': 'Universal Data Bridge',
  '/brand-assets': 'AI Design & Marketing',
};

export default function AppShell() {
  const { token, ready, tenant, user } = useAuth();
  const { profile, loading: tenantLoading } = useTenant();
  const location = useLocation();
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Dynamic PWA manifest with tenant branding (Phase 21)
  useDynamicManifest();

  // Dynamic browser tab title
  useEffect(() => {
    if (tenant?.name) {
      document.title = `1000AD | ${tenant.name}`;
    } else {
      document.title = DEFAULT_TITLE;
    }
    return () => { document.title = DEFAULT_TITLE; };
  }, [tenant?.name]);

  if (!ready) {
    return (
      <div className="h-screen w-screen bg-[#f7f8fb] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!tenantLoading && profile?.onboarding_completed === false && user?.role === 'owner') {
    return <Navigate to="/onboarding" replace />;
  }

  const isManagerPage = location.pathname.startsWith('/managers/');
  const managerSlug = isManagerPage ? location.pathname.split('/').pop() : '';
  const title = PAGE_TITLES[location.pathname]
    || (isManagerPage ? `AI ${managerSlug?.charAt(0).toUpperCase()}${managerSlug?.slice(1)}` : 'Home');

  return (
    <div className="flex h-screen bg-[#f7f8fb] overflow-hidden" data-testid="app-shell" data-theme="light">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar title={title} onRobotClick={() => setAssistantOpen(v => !v)} />
        <main className="flex-1 overflow-y-auto bg-[#f7f8fb]">
          <Outlet />
        </main>
      </div>
      {location.pathname !== '/assistant' && (
        <FloatingAssistant open={assistantOpen} onToggle={setAssistantOpen} />
      )}
    </div>
  );
}
